import { FontWeights, IIconProps, IconButton, getTheme, mergeStyleSets, SpinnerSize, Spinner } from '@fluentui/react';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import HelpComponentRelative from '../../Help/HelpComponentRelative';
import { isMobile } from "react-device-detect";
import DraftList from '../../DraftDocuments/DraftList';
import { useId } from "@uifabric/react-hooks";
import { getRecordDescription } from './CustomForm';
import { useBranding } from "../../../hooks/useBranding";
import { useDocument } from '../../../hooks/useDocument';
import UserService from '../../../services/User';
import { useModal } from '../../../hooks/useModal';
import { UpdateFormProfile } from '../../MyProfile/UpdateFormProfile';
import { IFormControl } from '../../../Helpers/Helper';
import { useSSO } from '../../../hooks/useSSO';
import { useDialog } from '../../../hooks/useDialog';

const theme = getTheme();

const previewIcon: IIconProps = { iconName: "DocumentSearch" };
const attachIcon: IIconProps = { iconName: "Attach" };
const cancelIcon: IIconProps = { iconName: "Cancel" };
type FormHeaderProps = {
    setApplying: (applying: boolean) => void;
    setLoading: (loading: boolean) => void;
    linkDraftId: number;
    handleCloseModal: () => void;
    isAppLinkDraft: boolean;
    allowDrafts: boolean;
    showAttachmentBtn: boolean;
    handleLoadDraft: (draft: any, callback: Function) => void
    draftId: number;
    templateId: string;
    username: string;
    isWizardPreview: boolean;
    onBehalf: string;
    createDraft: (draftId: number) => void
    preview: () => void;
    setShowAttachment: (showAttachment: boolean) => void;
    isUpdate: boolean
    recordDescription: string
    isAccessCodeValid: boolean
    setIsAccessCodeValid: (isAccessCodeValid: boolean) => void
    reloadProfile: () => void
    /** MODERNIZATION: public form-link page. Suppresses this component's own
     *  title (DocumentLink's page header already shows it) and relocates the
     *  action buttons into that header. */
    flat?: boolean
    /** Base64 PNG of the template's Application Image, shown as a top-left logo
     *  before the title (used by the wizard preview to match the link view). */
    appLogo?: string
};

export default function FormHeader({ setApplying, linkDraftId, handleCloseModal, isAppLinkDraft, setLoading, reloadProfile,
    allowDrafts, showAttachmentBtn, handleLoadDraft, draftId, templateId, username, isWizardPreview, recordDescription,
    onBehalf, createDraft, preview, setShowAttachment, isAccessCodeValid, flat, appLogo }: FormHeaderProps) {
    const { branding } = useBranding()
    const { isValid: isSSOValid } = useSSO()
    const { profile } = useDocument()
    const profileModal = useModal()
    const logoutDialog = useDialog()
    const [isLogginOut, setIsLoggingOut] = useState(false)
    const titleId = useId("CustomTemplate")

    const buttonHelpActionsStyles = {
        root: {
            color: branding.theme.palette.themePrimary,
            selectors: {
                ":hover": {
                    cursor: "pointer",
                },
            },
        },

    };

    const toolbarStyles = mergeStyleSets({
        header: [
            theme.fonts.xLarge,
            {
                flex: "1 1 auto",
                color: branding.theme.palette.black,
                display: "flex",
                alignItems: "center",
                fontWeight: FontWeights.semibold,
                padding: "12px 12px 14px 24px",
            },
        ],
        container: {
            display: "flex",
            justifyContent: isMobile ? 'flex-end' : "space-between",
        },
        draftButton: {
            paddingRight: isMobile ? '0px' : '25px',
            display: 'flex',
            height: '32px',
            alignItems: 'center'
        },
        actionsBar: {
            display: "flex",
            gap: "50px",
            alignItems: "center",
        },
        actions: {
            display: "flex",
            gap: "2px",
        },
        helpActions: {
            display: "flex",
            marginRight: '20px'
        }
    })

    /**
     * MODERNIZATION: in flat mode the action buttons are portaled into the page
     * header rendered by DocumentLink. The host lives in a different component,
     * so resolve it after mount and re-render once it is found.
     */
    const [topbarHost, setTopbarHost] = useState<HTMLElement | null>(null)
    useEffect(() => {
        if (!flat) return
        setTopbarHost(document.getElementById("fl-topbar-actions"))
    }, [flat])

    // Wizard-preview only: full-screen toggle. A body class drives CSS that expands
    // the preview modal to fill the viewport; cleaned up on unmount so the class
    // never leaks to other modals.
    const [isFullscreen, setIsFullscreen] = useState(false)
    useEffect(() => {
        if (!isWizardPreview) return
        document.body.classList.toggle("fl-preview-fullscreen", isFullscreen)
        return () => document.body.classList.remove("fl-preview-fullscreen")
    }, [isFullscreen, isWizardPreview])

    const logoutSSO = () => () => {
        if (!profile?.Email) {
            return
        }
        setIsLoggingOut(true)
        UserService.Logout(profile?.Email).then(() => {
        }).finally(() => {
            window.location.reload()
        })
    }

    const actionsBar = (
                <div className={`${toolbarStyles.actionsBar} ${flat ? "fl-form-actions" : ""}`}>
                    {!isWizardPreview && isAccessCodeValid ? (
                        <div className={toolbarStyles.actions}>

                            <div>
                                {isAppLinkDraft ?
                                    <div className={toolbarStyles.draftButton}>
                                        <DraftList CurrentDraftId={linkDraftId} TemplateId={templateId.toString()}
                                            handleLoadDraft={handleLoadDraft} onCreateUpdateDraft={() => { }}
                                            UserName={username} EndUserName={onBehalf} />
                                    </div>
                                    : null
                                }
                            </div>
                            {isSSOValid ? <>
                                <div>
                                    <IconButton
                                        className="tabIndexClose"
                                        styles={iconButtonStyles}
                                        iconProps={{ iconName: "EditContact", }}
                                        title={t("title.btnUpdateProfile", { ns: "wizard" })}
                                        onClick={() => {
                                            profileModal.open()
                                        }}
                                    />
                                </div>
                                <div>
                                    <IconButton
                                        className="tabIndexClose"
                                        styles={iconButtonStyles}
                                        iconProps={{ iconName: "SignOut", }}
                                        title={t("Common.Button.Logout", { ns: "common" })}
                                        onClick={() => {
                                            logoutDialog.open({ confirm: logoutSSO })
                                        }}
                                    />
                                </div>
                            </> : null}
                            <div>

                                {allowDrafts && (
                                    <IconButton
                                        className="tabIndexClose"
                                        styles={iconButtonStyles}
                                        iconProps={{ iconName: "SaveTemplate", }}
                                        title={t("title.btnSaveDraft", { ns: "wizard" })}

                                        onClick={() => {
                                            setApplying(true);
                                            createDraft(linkDraftId > 0 ? linkDraftId : draftId);
                                        }}
                                    />
                                )}
                            </div>
                            <div>
                                <IconButton
                                    className="tabIndexClose"
                                    styles={iconButtonStyles}
                                    iconProps={previewIcon}
                                    title={t("title.btnPreview", { ns: "wizard", })}
                                    ariaLabel={t("Common.Button.Close", { ns: "common" })}
                                    onClick={() => {
                                        preview();
                                    }}
                                />
                            </div>
                            <div>
                                {showAttachmentBtn && (

                                    <IconButton
                                        className="tabIndexClose"
                                        styles={iconButtonStyles}
                                        iconProps={attachIcon}
                                        title={t("title.btnAddAttachments", { ns: "wizard" })}
                                        ariaLabel={t("Common.Button.Close", { ns: "common" })}
                                        onClick={() => {
                                            setShowAttachment(true);
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    ) : null}
                    {!isMobile && <div className={toolbarStyles.helpActions}>
                        {isWizardPreview && (
                            <IconButton
                                className="tabIndexClose"
                                styles={buttonHelpActionsStyles}
                                iconProps={{ iconName: isFullscreen ? "BackToWindow" : "FullScreen" }}
                                title={isFullscreen ? "Exit full screen" : "Full screen"}
                                ariaLabel={isFullscreen ? "Exit full screen" : "Full screen"}
                                onClick={() => setIsFullscreen((v) => !v)}
                            />
                        )}
                        <IconButton
                            className="tabIndexClose"
                            styles={buttonHelpActionsStyles}
                            iconProps={cancelIcon}
                            title={t("Common.Button.Close", { ns: "common" })}
                            ariaLabel={t("Common.Button.Close", { ns: "common" })}
                            onClick={handleCloseModal}
                        />
                    </div>
                    }
                </div>
    )

    return (
        <>
            {flat ? (
                // Title intentionally omitted — DocumentLink's page header already
                // shows the template name, and rendering it again read as a
                // duplicate heading.
                topbarHost ? createPortal(actionsBar, topbarHost) : null
            ) : (
                <div className={toolbarStyles.container}>
                    {!isMobile && <div>
                        <div className={toolbarStyles.header}>
                            {appLogo ? (
                                <img
                                    src={`data:image/png;base64,${appLogo}`}
                                    alt=""
                                    style={{ maxHeight: 34, maxWidth: 160, width: "auto", marginRight: 12 }}
                                />
                            ) : null}
                            <span id={titleId}>{recordDescription}</span>
                        </div>
                    </div>
                    }
                    {actionsBar}
                </div>
            )}
            <profileModal.Modal title="Update profile" titleId="updateProfile_customForm" className="fl-shell fl-modern-modal">
                <UpdateFormProfile setLoading={setLoading} close={profileModal.close} reloadForm={reloadProfile} />
            </profileModal.Modal>
            {logoutDialog.isOpen && <logoutDialog.Dialog className="fl-shell fl-modern-modal" title={'Logout'} titleId='logoutSSOModal' showAction={!isLogginOut} actionText={t('Common.Button.Logout', { ns: 'common' })}>
                {isLogginOut ?
                    <Spinner size={SpinnerSize.large} ariaLive="assertive" />
                    : <>Are you sure you want to logout?</>}
            </logoutDialog.Dialog>}
        </>
    )
}

const iconButtonStyles = {

    root: {
        backgroundColor: isMobile ? 'transparent' : "lightgray",
        selectors: {
            ":hover": {
                cursor: "pointer",
            },
        },
    },
    rootHovered: {
        color: theme.palette.black,
        backgroundColor: isMobile ? 'transparent' : "lightgray",
    },
};